import { toSxArray } from '@shared/theme/util';
import { Select as SharedSelect } from '@shared/ui/Inputs';
import type { ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTexts } from '@/feature/lang';
import type { Translation } from '@/feature/lang/types';
import SelectSkeleton from './SelectSkeleton';
import type { Id, SelectProps } from './types';
import { useValidation } from './useValidation';

function Select<T extends Id>({
  margin = 'normal',
  variant = 'filled',
  fullWidth = true,
  label,
  loading,
  hideEmptyOption,
  options,
  renderOption,
  placeholder,
  registerControl,
  validation,
  rounded = false,
  ...rest
}: SelectProps<T>) {
  if (loading) {
    return <SelectSkeleton sx={toSxArray(rest.sx)} />;
  }

  const props = {
    ...rest,
    margin,
    variant,
    fullWidth,
    label,
    placeholder,
    rounded,
    options,
    showEmptyOption: !hideEmptyOption,
    emptyOptionLabel: '-',
    getOptionValue: (option: T) => option.id,
    getOptionLabel: getDefaultOptionLabel,
    renderOption: renderOption as ((option: T) => ReactNode) | undefined,
    slotProps: {
      ...rest.slotProps,
      input: {
        ...rest.slotProps?.input,
      },
      select: rest.slotProps?.select,
    },
  };

  return registerControl ? (
    <RegisteredControl {...props} validation={validation} />
  ) : (
    <Basic {...props} />
  );
}

export default Select;

function Basic({ label, ...rest }: any) {
  return <SharedSelect {...rest} label={label} />;
}

function RegisteredControl({
  name,
  validation: validationBase,
  label,
  ...rest
}: any) {
  const texts = useTexts();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const validation = useValidation(validationBase, { label });
  const error = name && errors[name];
  const helperText = error && (error.message || 'validation error');

  return (
    <Controller
      render={({ field }) => (
        <SharedSelect
          {...rest}
          error={Boolean(error)}
          helperText={helperText}
          label={label}
          requiredLabel={texts.component.form.label.required}
          showRequiredLabel={Boolean(validation?.required)}
          {...field}
        />
      )}
      control={control}
      name={name}
      rules={validation}
      defaultValue=""
    />
  );
}

function getDefaultOptionLabel(option: any) {
  if (!option) {
    return undefined;
  }
  if (!Object.keys(option).includes('title')) {
    console.warn(
      'The default option renderer for select fields is used. Its using the' +
        ' prop "title" as display value but the options lacks it. Customize' +
        ' the layout of the options by providing a "defaultRenderOption"' +
        ' prop to the select.',
      option
    );
  }
  if (option.title) {
    return option.title as Translation | ReactNode;
  }
  return option.id;
}
